import React, { useState } from 'react';
import { api } from '@/api/apiClient';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, Loader2, MapPin } from 'lucide-react';
import { toast } from 'sonner';

// Complete 2024 State Tax Data - All 50 States + DC
const ALL_STATES_2024 = {
  // No Income Tax States (7)
  noTaxStates: ['AK', 'FL', 'NV', 'SD', 'TN', 'TX', 'WY'],
  
  // Flat Rate States (12)
  flatRateStates: {
    AZ: 0.025,
    CO: 0.044,
    GA: 0.0549,
    ID: 0.058,
    IL: 0.0495,
    IN: 0.031,
    KY: 0.04,
    MI: 0.0425,
    MS: 0.05,
    NC: 0.0475,
    PA: 0.0307,
    UT: 0.0465
  },
  
  // Progressive States with brackets
  progressiveStates: {
    AL: {
      single: { deduction: 2500, brackets: [
        { min: 0, max: 500, rate: 0.02, base: 0 },
        { min: 500, max: 3000, rate: 0.04, base: 10 },
        { min: 3000, max: 999999999, rate: 0.05, base: 110 }
      ]},
      married: { deduction: 7500, brackets: [
        { min: 0, max: 1000, rate: 0.02, base: 0 },
        { min: 1000, max: 6000, rate: 0.04, base: 20 },
        { min: 6000, max: 999999999, rate: 0.05, base: 220 }
      ]}
    },
    AR: {
      single: { deduction: 2340, brackets: [
        { min: 0, max: 5099, rate: 0.02, base: 0 },
        { min: 5099, max: 10299, rate: 0.03, base: 102 },
        { min: 10299, max: 999999999, rate: 0.039, base: 258 }
      ]},
      married: { deduction: 4680, brackets: [
        { min: 0, max: 5099, rate: 0.02, base: 0 },
        { min: 5099, max: 10299, rate: 0.03, base: 102 },
        { min: 10299, max: 999999999, rate: 0.039, base: 258 }
      ]}
    },
    CA: {
      single: { deduction: 5202, brackets: [
        { min: 0, max: 10412, rate: 0.01, base: 0 },
        { min: 10412, max: 24684, rate: 0.02, base: 104.12 },
        { min: 24684, max: 38959, rate: 0.04, base: 389.56 },
        { min: 38959, max: 54081, rate: 0.06, base: 960.56 },
        { min: 54081, max: 68350, rate: 0.08, base: 1867.88 },
        { min: 68350, max: 349137, rate: 0.093, base: 3009.40 },
        { min: 349137, max: 418961, rate: 0.103, base: 29121.60 },
        { min: 418961, max: 698271, rate: 0.113, base: 36313.48 },
        { min: 698271, max: 999999999, rate: 0.133, base: 67878.51 }
      ]},
      married: { deduction: 10404, brackets: [
        { min: 0, max: 20824, rate: 0.01, base: 0 },
        { min: 20824, max: 49368, rate: 0.02, base: 208.24 },
        { min: 49368, max: 77918, rate: 0.04, base: 779.12 },
        { min: 77918, max: 108162, rate: 0.06, base: 1921.12 },
        { min: 108162, max: 136700, rate: 0.08, base: 3735.76 },
        { min: 136700, max: 698274, rate: 0.093, base: 6018.80 },
        { min: 698274, max: 837922, rate: 0.103, base: 58243.20 },
        { min: 837922, max: 1396542, rate: 0.113, base: 72626.96 },
        { min: 1396542, max: 999999999, rate: 0.133, base: 135757.02 }
      ]}
    },
    CT: {
      single: { deduction: 0, brackets: [
        { min: 0, max: 10000, rate: 0.03, base: 0 },
        { min: 10000, max: 50000, rate: 0.05, base: 300 },
        { min: 50000, max: 100000, rate: 0.055, base: 2300 },
        { min: 100000, max: 200000, rate: 0.06, base: 5050 },
        { min: 200000, max: 250000, rate: 0.065, base: 11050 },
        { min: 250000, max: 500000, rate: 0.069, base: 14300 },
        { min: 500000, max: 999999999, rate: 0.0699, base: 31550 }
      ]},
      married: { deduction: 0, brackets: [
        { min: 0, max: 20000, rate: 0.03, base: 0 },
        { min: 20000, max: 100000, rate: 0.05, base: 600 },
        { min: 100000, max: 200000, rate: 0.055, base: 4600 },
        { min: 200000, max: 400000, rate: 0.06, base: 10100 },
        { min: 400000, max: 500000, rate: 0.065, base: 22100 },
        { min: 500000, max: 1000000, rate: 0.069, base: 28600 },
        { min: 1000000, max: 999999999, rate: 0.0699, base: 63100 }
      ]}
    },
    DE: {
      single: { deduction: 3250, brackets: [
        { min: 0, max: 2000, rate: 0.022, base: 0 },
        { min: 2000, max: 5000, rate: 0.039, base: 44 },
        { min: 5000, max: 10000, rate: 0.048, base: 161 },
        { min: 10000, max: 20000, rate: 0.052, base: 401 },
        { min: 20000, max: 25000, rate: 0.0555, base: 921 },
        { min: 25000, max: 60000, rate: 0.066, base: 1198.50 },
        { min: 60000, max: 999999999, rate: 0.066, base: 3508.50 }
      ]},
      married: { deduction: 6500, brackets: [
        { min: 0, max: 2000, rate: 0.022, base: 0 },
        { min: 2000, max: 5000, rate: 0.039, base: 44 },
        { min: 5000, max: 10000, rate: 0.048, base: 161 },
        { min: 10000, max: 20000, rate: 0.052, base: 401 },
        { min: 20000, max: 25000, rate: 0.0555, base: 921 },
        { min: 25000, max: 60000, rate: 0.066, base: 1198.50 },
        { min: 60000, max: 999999999, rate: 0.066, base: 3508.50 }
      ]}
    },
    HI: {
      single: { deduction: 2200, brackets: [
        { min: 0, max: 2400, rate: 0.014, base: 0 },
        { min: 2400, max: 4800, rate: 0.032, base: 33.60 },
        { min: 4800, max: 9600, rate: 0.055, base: 110.40 },
        { min: 9600, max: 14400, rate: 0.064, base: 374.40 },
        { min: 14400, max: 19200, rate: 0.068, base: 681.60 },
        { min: 19200, max: 24000, rate: 0.072, base: 1008 },
        { min: 24000, max: 36000, rate: 0.076, base: 1353.60 },
        { min: 36000, max: 48000, rate: 0.079, base: 2265.60 },
        { min: 48000, max: 150000, rate: 0.0825, base: 3213.60 },
        { min: 150000, max: 175000, rate: 0.09, base: 11628.60 },
        { min: 175000, max: 200000, rate: 0.10, base: 13878.60 },
        { min: 200000, max: 999999999, rate: 0.11, base: 16378.60 }
      ]},
      married: { deduction: 4400, brackets: [
        { min: 0, max: 4800, rate: 0.014, base: 0 },
        { min: 4800, max: 9600, rate: 0.032, base: 67.20 },
        { min: 9600, max: 19200, rate: 0.055, base: 220.80 },
        { min: 19200, max: 28800, rate: 0.064, base: 748.80 },
        { min: 28800, max: 38400, rate: 0.068, base: 1363.20 },
        { min: 38400, max: 48000, rate: 0.072, base: 2016 },
        { min: 48000, max: 72000, rate: 0.076, base: 2707.20 },
        { min: 72000, max: 96000, rate: 0.079, base: 4531.20 },
        { min: 96000, max: 300000, rate: 0.0825, base: 6427.20 },
        { min: 300000, max: 350000, rate: 0.09, base: 23257.20 },
        { min: 350000, max: 400000, rate: 0.10, base: 27757.20 },
        { min: 400000, max: 999999999, rate: 0.11, base: 32757.20 }
      ]}
    },
    IA: {
      single: { deduction: 2130, brackets: [
        { min: 0, max: 6000, rate: 0.044, base: 0 },
        { min: 6000, max: 30000, rate: 0.0482, base: 264 },
        { min: 30000, max: 75000, rate: 0.057, base: 1420.80 },
        { min: 75000, max: 999999999, rate: 0.06, base: 3985.80 }
      ]},
      married: { deduction: 5240, brackets: [
        { min: 0, max: 12000, rate: 0.044, base: 0 },
        { min: 12000, max: 60000, rate: 0.0482, base: 528 },
        { min: 60000, max: 150000, rate: 0.057, base: 2841.60 },
        { min: 150000, max: 999999999, rate: 0.06, base: 7971.60 }
      ]}
    },
    KS: {
      single: { deduction: 3500, brackets: [
        { min: 0, max: 15000, rate: 0.031, base: 0 },
        { min: 15000, max: 30000, rate: 0.0525, base: 465 },
        { min: 30000, max: 999999999, rate: 0.057, base: 1252.50 }
      ]},
      married: { deduction: 8000, brackets: [
        { min: 0, max: 30000, rate: 0.031, base: 0 },
        { min: 30000, max: 60000, rate: 0.0525, base: 930 },
        { min: 60000, max: 999999999, rate: 0.057, base: 2505 }
      ]}
    },
    LA: {
      single: { deduction: 0, brackets: [
        { min: 0, max: 12500, rate: 0.0185, base: 0 },
        { min: 12500, max: 50000, rate: 0.035, base: 231.25 },
        { min: 50000, max: 999999999, rate: 0.0425, base: 1543.75 }
      ]},
      married: { deduction: 0, brackets: [
        { min: 0, max: 25000, rate: 0.0185, base: 0 },
        { min: 25000, max: 100000, rate: 0.035, base: 462.50 },
        { min: 100000, max: 999999999, rate: 0.0425, base: 3087.50 }
      ]}
    },
    ME: {
      single: { deduction: 13850, brackets: [
        { min: 0, max: 24500, rate: 0.058, base: 0 },
        { min: 24500, max: 58050, rate: 0.0675, base: 1421 },
        { min: 58050, max: 999999999, rate: 0.0715, base: 3686.13 }
      ]},
      married: { deduction: 27700, brackets: [
        { min: 0, max: 49050, rate: 0.058, base: 0 },
        { min: 49050, max: 116100, rate: 0.0675, base: 2844.90 },
        { min: 116100, max: 999999999, rate: 0.0715, base: 7370.28 }
      ]}
    },
    MD: {
      single: { deduction: 2550, brackets: [
        { min: 0, max: 1000, rate: 0.02, base: 0 },
        { min: 1000, max: 2000, rate: 0.03, base: 20 },
        { min: 2000, max: 3000, rate: 0.04, base: 50 },
        { min: 3000, max: 100000, rate: 0.0475, base: 90 },
        { min: 100000, max: 125000, rate: 0.05, base: 4697.50 },
        { min: 125000, max: 150000, rate: 0.0525, base: 5947.50 },
        { min: 150000, max: 250000, rate: 0.055, base: 7260 },
        { min: 250000, max: 999999999, rate: 0.0575, base: 12760 }
      ]},
      married: { deduction: 5100, brackets: [
        { min: 0, max: 1000, rate: 0.02, base: 0 },
        { min: 1000, max: 2000, rate: 0.03, base: 20 },
        { min: 2000, max: 3000, rate: 0.04, base: 50 },
        { min: 3000, max: 150000, rate: 0.0475, base: 90 },
        { min: 150000, max: 175000, rate: 0.05, base: 7072.50 },
        { min: 175000, max: 225000, rate: 0.0525, base: 8322.50 },
        { min: 225000, max: 300000, rate: 0.055, base: 10947.50 },
        { min: 300000, max: 999999999, rate: 0.0575, base: 15072.50 }
      ]}
    },
    MA: {
      single: { deduction: 0, brackets: [
        { min: 0, max: 1000000, rate: 0.05, base: 0 },
        { min: 1000000, max: 999999999, rate: 0.09, base: 50000 }
      ]},
      married: { deduction: 0, brackets: [
        { min: 0, max: 1000000, rate: 0.05, base: 0 },
        { min: 1000000, max: 999999999, rate: 0.09, base: 50000 }
      ]}
    },
    MN: {
      single: { deduction: 14600, brackets: [
        { min: 0, max: 31690, rate: 0.0535, base: 0 },
        { min: 31690, max: 104090, rate: 0.068, base: 1695.42 },
        { min: 104090, max: 183340, rate: 0.0785, base: 6618.62 },
        { min: 183340, max: 999999999, rate: 0.0985, base: 12840.23 }
      ]},
      married: { deduction: 29200, brackets: [
        { min: 0, max: 46690, rate: 0.0535, base: 0 },
        { min: 46690, max: 185650, rate: 0.068, base: 2497.92 },
        { min: 185650, max: 304970, rate: 0.0785, base: 11943.20 },
        { min: 304970, max: 999999999, rate: 0.0985, base: 21313.32 }
      ]}
    },
    MO: {
      single: { deduction: 14600, brackets: [
        { min: 0, max: 1207, rate: 0.0, base: 0 },
        { min: 1207, max: 2414, rate: 0.02, base: 0 },
        { min: 2414, max: 3621, rate: 0.025, base: 24.14 },
        { min: 3621, max: 4828, rate: 0.03, base: 54.32 },
        { min: 4828, max: 6035, rate: 0.035, base: 90.53 },
        { min: 6035, max: 7242, rate: 0.04, base: 132.78 },
        { min: 7242, max: 8449, rate: 0.045, base: 181.06 },
        { min: 8449, max: 999999999, rate: 0.0485, base: 235.38 }
      ]},
      married: { deduction: 29200, brackets: [
        { min: 0, max: 1207, rate: 0.0, base: 0 },
        { min: 1207, max: 2414, rate: 0.02, base: 0 },
        { min: 2414, max: 3621, rate: 0.025, base: 24.14 },
        { min: 3621, max: 4828, rate: 0.03, base: 54.32 },
        { min: 4828, max: 6035, rate: 0.035, base: 90.53 },
        { min: 6035, max: 7242, rate: 0.04, base: 132.78 },
        { min: 7242, max: 8449, rate: 0.045, base: 181.06 },
        { min: 8449, max: 999999999, rate: 0.0485, base: 235.38 }
      ]}
    },
    MT: {
      single: { deduction: 5340, brackets: [
        { min: 0, max: 21100, rate: 0.0485, base: 0 },
        { min: 21100, max: 999999999, rate: 0.0585, base: 1023.35 }
      ]},
      married: { deduction: 10680, brackets: [
        { min: 0, max: 21100, rate: 0.0485, base: 0 },
        { min: 21100, max: 999999999, rate: 0.0585, base: 1023.35 }
      ]}
    },
    NE: {
      single: { deduction: 8100, brackets: [
        { min: 0, max: 3700, rate: 0.0246, base: 0 },
        { min: 3700, max: 22170, rate: 0.0351, base: 91.02 },
        { min: 22170, max: 35730, rate: 0.0501, base: 739.11 },
        { min: 35730, max: 999999999, rate: 0.0664, base: 1418.17 }
      ]},
      married: { deduction: 16200, brackets: [
        { min: 0, max: 7390, rate: 0.0246, base: 0 },
        { min: 7390, max: 44350, rate: 0.0351, base: 181.79 },
        { min: 44350, max: 71460, rate: 0.0501, base: 1479.50 },
        { min: 71460, max: 999999999, rate: 0.0664, base: 2837.01 }
      ]}
    },
    NJ: {
      single: { deduction: 0, brackets: [
        { min: 0, max: 20000, rate: 0.014, base: 0 },
        { min: 20000, max: 35000, rate: 0.0175, base: 280 },
        { min: 35000, max: 40000, rate: 0.035, base: 542.50 },
        { min: 40000, max: 75000, rate: 0.05525, base: 717.50 },
        { min: 75000, max: 500000, rate: 0.0637, base: 2651.25 },
        { min: 500000, max: 1000000, rate: 0.0897, base: 29723.75 },
        { min: 1000000, max: 999999999, rate: 0.1075, base: 74573.75 }
      ]},
      married: { deduction: 0, brackets: [
        { min: 0, max: 20000, rate: 0.014, base: 0 },
        { min: 20000, max: 50000, rate: 0.0175, base: 280 },
        { min: 50000, max: 70000, rate: 0.0245, base: 805 },
        { min: 70000, max: 80000, rate: 0.035, base: 1295 },
        { min: 80000, max: 150000, rate: 0.05525, base: 1645 },
        { min: 150000, max: 500000, rate: 0.0637, base: 5512.50 },
        { min: 500000, max: 1000000, rate: 0.0897, base: 27807.50 },
        { min: 1000000, max: 999999999, rate: 0.1075, base: 72657.50 }
      ]}
    },
    NM: {
      single: { deduction: 14600, brackets: [
        { min: 0, max: 5500, rate: 0.017, base: 0 },
        { min: 5500, max: 11000, rate: 0.032, base: 93.50 },
        { min: 11000, max: 16000, rate: 0.047, base: 269.50 },
        { min: 16000, max: 210000, rate: 0.049, base: 504.50 },
        { min: 210000, max: 999999999, rate: 0.059, base: 10010.50 }
      ]},
      married: { deduction: 29200, brackets: [
        { min: 0, max: 8000, rate: 0.017, base: 0 },
        { min: 8000, max: 16000, rate: 0.032, base: 136 },
        { min: 16000, max: 24000, rate: 0.047, base: 392 },
        { min: 24000, max: 315000, rate: 0.049, base: 768 },
        { min: 315000, max: 999999999, rate: 0.059, base: 15027 }
      ]}
    },
    NY: {
      single: { deduction: 8000, brackets: [
        { min: 0, max: 8500, rate: 0.04, base: 0 },
        { min: 8500, max: 11700, rate: 0.045, base: 340 },
        { min: 11700, max: 13900, rate: 0.0525, base: 484 },
        { min: 13900, max: 80650, rate: 0.055, base: 599.50 },
        { min: 80650, max: 215400, rate: 0.06, base: 4270.75 },
        { min: 215400, max: 1077550, rate: 0.0685, base: 12355.75 },
        { min: 1077550, max: 5000000, rate: 0.0965, base: 71405.03 },
        { min: 5000000, max: 25000000, rate: 0.103, base: 449976.28 },
        { min: 25000000, max: 999999999, rate: 0.109, base: 2509976.28 }
      ]},
      married: { deduction: 16050, brackets: [
        { min: 0, max: 17150, rate: 0.04, base: 0 },
        { min: 17150, max: 23600, rate: 0.045, base: 686 },
        { min: 23600, max: 27900, rate: 0.0525, base: 976.25 },
        { min: 27900, max: 161550, rate: 0.055, base: 1202.00 },
        { min: 161550, max: 323200, rate: 0.06, base: 8552.75 },
        { min: 323200, max: 2155350, rate: 0.0685, base: 18251.75 },
        { min: 2155350, max: 5000000, rate: 0.0965, base: 143753.03 },
        { min: 5000000, max: 25000000, rate: 0.103, base: 418201.78 },
        { min: 25000000, max: 999999999, rate: 0.109, base: 2478201.78 }
      ]}
    },
    ND: {
      single: { deduction: 14600, brackets: [
        { min: 0, max: 44725, rate: 0.0, base: 0 },
        { min: 44725, max: 225975, rate: 0.0225, base: 0 },
        { min: 225975, max: 999999999, rate: 0.025, base: 4078.13 }
      ]},
      married: { deduction: 29200, brackets: [
        { min: 0, max: 74750, rate: 0.0, base: 0 },
        { min: 74750, max: 275100, rate: 0.0225, base: 0 },
        { min: 275100, max: 999999999, rate: 0.025, base: 4507.88 }
      ]}
    },
    OH: {
      single: { deduction: 0, brackets: [
        { min: 0, max: 26050, rate: 0.0, base: 0 },
        { min: 26050, max: 100000, rate: 0.02765, base: 0 },
        { min: 100000, max: 115300, rate: 0.03226, base: 2044.24 },
        { min: 115300, max: 999999999, rate: 0.03688, base: 2537.82 }
      ]},
      married: { deduction: 0, brackets: [
        { min: 0, max: 26050, rate: 0.0, base: 0 },
        { min: 26050, max: 100000, rate: 0.02765, base: 0 },
        { min: 100000, max: 115300, rate: 0.03226, base: 2044.24 },
        { min: 115300, max: 999999999, rate: 0.03688, base: 2537.82 }
      ]}
    },
    OK: {
      single: { deduction: 6350, brackets: [
        { min: 0, max: 1000, rate: 0.0025, base: 0 },
        { min: 1000, max: 2500, rate: 0.0075, base: 2.50 },
        { min: 2500, max: 3750, rate: 0.0175, base: 13.75 },
        { min: 3750, max: 4900, rate: 0.0275, base: 35.63 },
        { min: 4900, max: 7200, rate: 0.0375, base: 67.25 },
        { min: 7200, max: 999999999, rate: 0.0475, base: 153.50 }
      ]},
      married: { deduction: 12700, brackets: [
        { min: 0, max: 2000, rate: 0.0025, base: 0 },
        { min: 2000, max: 5000, rate: 0.0075, base: 5 },
        { min: 5000, max: 7500, rate: 0.0175, base: 27.50 },
        { min: 7500, max: 9800, rate: 0.0275, base: 71.25 },
        { min: 9800, max: 12200, rate: 0.0375, base: 134.50 },
        { min: 12200, max: 999999999, rate: 0.0475, base: 224.50 }
      ]}
    },
    OR: {
      single: { deduction: 2605, brackets: [
        { min: 0, max: 4050, rate: 0.0475, base: 0 },
        { min: 4050, max: 10200, rate: 0.0675, base: 192.38 },
        { min: 10200, max: 125000, rate: 0.0875, base: 607.50 },
        { min: 125000, max: 999999999, rate: 0.099, base: 10647.50 }
      ]},
      married: { deduction: 5210, brackets: [
        { min: 0, max: 8100, rate: 0.0475, base: 0 },
        { min: 8100, max: 20350, rate: 0.0675, base: 384.75 },
        { min: 20350, max: 250000, rate: 0.0875, base: 1211.63 },
        { min: 250000, max: 999999999, rate: 0.099, base: 21306.25 }
      ]}
    },
    RI: {
      single: { deduction: 10400, brackets: [
        { min: 0, max: 73450, rate: 0.0375, base: 0 },
        { min: 73450, max: 166950, rate: 0.0475, base: 2754.38 },
        { min: 166950, max: 999999999, rate: 0.0599, base: 7195.63 }
      ]},
      married: { deduction: 20800, brackets: [
        { min: 0, max: 73450, rate: 0.0375, base: 0 },
        { min: 73450, max: 166950, rate: 0.0475, base: 2754.38 },
        { min: 166950, max: 999999999, rate: 0.0599, base: 7195.63 }
      ]}
    },
    SC: {
      single: { deduction: 14600, brackets: [
        { min: 0, max: 3200, rate: 0.0, base: 0 },
        { min: 3200, max: 16040, rate: 0.03, base: 0 },
        { min: 16040, max: 999999999, rate: 0.064, base: 385.20 }
      ]},
      married: { deduction: 29200, brackets: [
        { min: 0, max: 3200, rate: 0.0, base: 0 },
        { min: 3200, max: 16040, rate: 0.03, base: 0 },
        { min: 16040, max: 999999999, rate: 0.064, base: 385.20 }
      ]}
    },
    VT: {
      single: { deduction: 7400, brackets: [
        { min: 0, max: 45400, rate: 0.0335, base: 0 },
        { min: 45400, max: 110050, rate: 0.066, base: 1520.90 },
        { min: 110050, max: 229550, rate: 0.076, base: 5787.80 },
        { min: 229550, max: 999999999, rate: 0.0875, base: 14869.80 }
      ]},
      married: { deduction: 14800, brackets: [
        { min: 0, max: 75850, rate: 0.0335, base: 0 },
        { min: 75850, max: 183400, rate: 0.066, base: 2540.98 },
        { min: 183400, max: 279450, rate: 0.076, base: 9639.28 },
        { min: 279450, max: 999999999, rate: 0.0875, base: 16939.08 }
      ]}
    },
    VA: {
      single: { deduction: 4500, brackets: [
        { min: 0, max: 3000, rate: 0.02, base: 0 },
        { min: 3000, max: 5000, rate: 0.03, base: 60 },
        { min: 5000, max: 17000, rate: 0.05, base: 120 },
        { min: 17000, max: 999999999, rate: 0.0575, base: 720 }
      ]},
      married: { deduction: 9000, brackets: [
        { min: 0, max: 3000, rate: 0.02, base: 0 },
        { min: 3000, max: 5000, rate: 0.03, base: 60 },
        { min: 5000, max: 17000, rate: 0.05, base: 120 },
        { min: 17000, max: 999999999, rate: 0.0575, base: 720 }
      ]}
    },
    WV: {
      single: { deduction: 0, brackets: [
        { min: 0, max: 10000, rate: 0.0236, base: 0 },
        { min: 10000, max: 25000, rate: 0.0315, base: 236 },
        { min: 25000, max: 40000, rate: 0.0354, base: 708.50 },
        { min: 40000, max: 60000, rate: 0.0472, base: 1239.50 },
        { min: 60000, max: 999999999, rate: 0.051, base: 2183.50 }
      ]},
      married: { deduction: 0, brackets: [
        { min: 0, max: 10000, rate: 0.0236, base: 0 },
        { min: 10000, max: 25000, rate: 0.0315, base: 236 },
        { min: 25000, max: 40000, rate: 0.0354, base: 708.50 },
        { min: 40000, max: 60000, rate: 0.0472, base: 1239.50 },
        { min: 60000, max: 999999999, rate: 0.051, base: 2183.50 }
      ]}
    },
    WI: {
      single: { deduction: 14600, brackets: [
        { min: 0, max: 13810, rate: 0.0354, base: 0 },
        { min: 13810, max: 27630, rate: 0.0465, base: 488.87 },
        { min: 27630, max: 304170, rate: 0.0627, base: 1131.00 },
        { min: 304170, max: 999999999, rate: 0.0765, base: 18465.05 }
      ]},
      married: { deduction: 29200, brackets: [
        { min: 0, max: 18420, rate: 0.0354, base: 0 },
        { min: 18420, max: 36840, rate: 0.0465, base: 652.07 },
        { min: 36840, max: 405550, rate: 0.0627, base: 1508.60 },
        { min: 405550, max: 999999999, rate: 0.0765, base: 24624.13 }
      ]}
    },
    DC: {
      single: { deduction: 14600, brackets: [
        { min: 0, max: 10000, rate: 0.04, base: 0 },
        { min: 10000, max: 40000, rate: 0.06, base: 400 },
        { min: 40000, max: 60000, rate: 0.065, base: 2200 },
        { min: 60000, max: 250000, rate: 0.085, base: 3500 },
        { min: 250000, max: 500000, rate: 0.0925, base: 19650 },
        { min: 500000, max: 1000000, rate: 0.0975, base: 42775 },
        { min: 1000000, max: 999999999, rate: 0.1075, base: 91525 }
      ]},
      married: { deduction: 29200, brackets: [
        { min: 0, max: 10000, rate: 0.04, base: 0 },
        { min: 10000, max: 40000, rate: 0.06, base: 400 },
        { min: 40000, max: 60000, rate: 0.065, base: 2200 },
        { min: 60000, max: 250000, rate: 0.085, base: 3500 },
        { min: 250000, max: 500000, rate: 0.0925, base: 19650 },
        { min: 500000, max: 1000000, rate: 0.0975, base: 42775 },
        { min: 1000000, max: 999999999, rate: 0.1075, base: 91525 }
      ]}
    }
  }
};

export default function AllStatesTaxSeedData({ onComplete }) {
  const [seeding, setSeeding] = useState(false);
  const [seeded, setSeeded] = useState(false);

  const generateTaxConfigs = () => {
    const configs = [];
    
    // No income tax states
    ALL_STATES_2024.noTaxStates.forEach(state => {
      ['single', 'married'].forEach(filing => {
        configs.push({
          tax_year: 2024,
          jurisdiction: 'state',
          state_code: state,
          filing_status: filing,
          flat_rate: 0,
          standard_deduction: 0,
          tax_brackets: [],
          is_active: true,
          notes: `2024 ${state} - No State Income Tax`
        });
      });
    });
    
    // Flat rate states
    Object.entries(ALL_STATES_2024.flatRateStates).forEach(([state, rate]) => {
      ['single', 'married'].forEach(filing => {
        configs.push({
          tax_year: 2024,
          jurisdiction: 'state',
          state_code: state,
          filing_status: filing,
          flat_rate: rate,
          standard_deduction: 0,
          is_active: true,
          notes: `2024 ${state} Flat Rate - ${(rate * 100).toFixed(2)}%`
        });
      });
    });
    
    // Progressive states
    Object.entries(ALL_STATES_2024.progressiveStates).forEach(([state, data]) => {
      configs.push({
        tax_year: 2024,
        jurisdiction: 'state',
        state_code: state,
        filing_status: 'single',
        standard_deduction: data.single.deduction,
        tax_brackets: data.single.brackets,
        is_active: true,
        notes: `2024 ${state} Tax Rates - Single Filers`
      });
      
      configs.push({
        tax_year: 2024,
        jurisdiction: 'state',
        state_code: state,
        filing_status: 'married',
        standard_deduction: data.married.deduction,
        tax_brackets: data.married.brackets,
        is_active: true,
        notes: `2024 ${state} Tax Rates - Married Filing Jointly`
      });
    });
    
    return configs;
  };

  const seedData = async () => {
    setSeeding(true);
    try {
      const configs = generateTaxConfigs();
      
      for (const config of configs) {
        await api.entities.TaxRateConfig.create(config);
      }
      
      toast.success(`Successfully seeded 2024 tax rates for all 50 states + DC`);
      setSeeded(true);
      if (onComplete) onComplete();
    } catch (err) {
      console.error(err);
      toast.error('Failed to seed state tax rates');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <Card className="border-2 border-purple-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-purple-600" />
          Seed ALL State Tax Rates (2024)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-700 font-medium">
          Complete 2024 state tax data for all 50 states + DC
        </p>
        
        <div className="text-xs text-slate-600 space-y-1 bg-slate-50 p-3 rounded-lg">
          <p>âœ“ 7 No-tax states (AK, FL, NV, SD, TN, TX, WY)</p>
          <p>âœ“ 12 Flat-rate states</p>
          <p>âœ“ 31 Progressive bracket states + DC</p>
          <p className="font-medium text-emerald-600 mt-2">Total: 102 tax configurations</p>
        </div>
        
        {seeded ? (
          <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 p-3 rounded-lg">
            <CheckCircle2 className="w-5 h-5" />
            <span className="font-medium">All state tax rates seeded!</span>
          </div>
        ) : (
          <Button 
            onClick={seedData} 
            disabled={seeding}
            className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600"
          >
            {seeding ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Seeding all states...
              </>
            ) : (
              <>
                <MapPin className="w-4 h-4 mr-2" />
                Seed All 50 States + DC (102 configs)
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}